import { detectAndSanitizePromptInjection, sanitizeUserInput } from "./sanitize";
import { countWords } from "./validation";
import { estimateBase64SizeBytes, recordDailyComputeUsage } from "./ocrRateLimiter";
import { getUserSessionId } from "./authSession";

export interface ExtractionResult {
  success: boolean;
  text: string;
  filename: string;
  fileType: "text" | "docx" | "pdf" | "image";
  wordCount: number;
  characterCount: number;
  warning?: string;
  error?: string;
}

const DANGEROUS_EXTENSIONS = [
  ".exe", ".bat", ".cmd", ".sh", ".php", ".js", ".html", ".htm", ".vbs",
  ".jar", ".py", ".pl", ".rb", ".cgi", ".phtml", ".phar", ".dll", ".so", ".bin"
];

const MAX_DOCUMENT_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB

/**
 * Parses dropped or selected document files (.txt, .md, .docx, .pdf, image)
 * and extracts clean, sanitized plain text for the Document Detector.
 */
export async function extractTextFromDocument(file: File): Promise<ExtractionResult> {
  const filename = file.name || "Uploaded-Document";
  const lowerName = filename.toLowerCase();

  // 1. Security Check: Reject dangerous extensions
  if (DANGEROUS_EXTENSIONS.some((ext) => lowerName.endsWith(ext))) {
    return {
      success: false,
      text: "",
      filename,
      fileType: "text",
      wordCount: 0,
      characterCount: 0,
      error: `Security Alert: Script or executable file type detected (${filename}). Only documents (.txt, .md, .docx, .pdf, images) are allowed.`
    };
  }

  // 2. Size Check
  if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
    const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
    return {
      success: false,
      text: "",
      filename,
      fileType: "text",
      wordCount: 0,
      characterCount: 0,
      error: `File size (${sizeMb} MB) exceeds maximum allowed limit of 20 MB.`
    };
  }

  if (file.size === 0) {
    return {
      success: false,
      text: "",
      filename,
      fileType: "text",
      wordCount: 0,
      characterCount: 0,
      error: "The selected file is empty (0 bytes)."
    };
  }

  // 3. Routing by File Format
  try {
    // A. Microsoft Word (.docx)
    if (lowerName.endsWith(".docx") || file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
      const arrayBuffer = await file.arrayBuffer();
      const mammoth = (await import("mammoth")).default;
      const result = await mammoth.extractRawText({ arrayBuffer });
      const rawText = result.value || "";

      if (!rawText.trim()) {
        return {
          success: false,
          text: "",
          filename,
          fileType: "docx",
          wordCount: 0,
          characterCount: 0,
          error: "Could not find readable text in this Word document. It may consist entirely of embedded images."
        };
      }

      const clean = sanitizeExtractedContent(rawText);
      const words = countWords(clean);

      return {
        success: true,
        text: clean,
        filename,
        fileType: "docx",
        wordCount: words,
        characterCount: clean.length,
        warning: result.messages.length > 0 ? "Some complex Word formatting was flattened to plain text." : undefined
      };
    }

    // B. Plain Text / Markdown / Code / Data files
    if (
      lowerName.endsWith(".txt") ||
      lowerName.endsWith(".md") ||
      lowerName.endsWith(".markdown") ||
      lowerName.endsWith(".csv") ||
      lowerName.endsWith(".rtf") ||
      lowerName.endsWith(".json") ||
      file.type.startsWith("text/")
    ) {
      const rawText = await file.text();
      if (!rawText.trim()) {
        return {
          success: false,
          text: "",
          filename,
          fileType: "text",
          wordCount: 0,
          characterCount: 0,
          error: "The text document contains no readable characters."
        };
      }

      const clean = sanitizeExtractedContent(rawText);
      const words = countWords(clean);

      return {
        success: true,
        text: clean,
        filename,
        fileType: "text",
        wordCount: words,
        characterCount: clean.length
      };
    }

    // C. PDF Document or Image File (PNG, JPG, WEBP, etc.)
    const isPdf = lowerName.endsWith(".pdf") || file.type === "application/pdf";
    const isImage = file.type.startsWith("image/") || /\.(png|jpe?g|webp|bmp|gif)$/i.test(lowerName);

    if (isPdf || isImage) {
      const dataUrl = await readFileAsDataUrl(file);
      let payload = dataUrl;

      if (isImage) {
        try {
          const { optimizeImageForOcr } = await import("./fastOcr");
          const optimized = await optimizeImageForOcr(dataUrl, 1400, 0.88);
          payload = optimized.dataUrl;
        } catch {
          // fallback to raw
        }
      }

      let rawText = "";

      try {
        const response = await fetch("/api/ocr-gemini", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Device-Id": getUserSessionId()
          },
          body: JSON.stringify({
            image: payload,
            filename
          })
        });

        const data = await response.json();
        if (response.ok && data.text) {
          rawText = data.text.trim();
        } else if (isImage) {
          const { runFastLocalOcr } = await import("./fastOcr");
          const localResult = await runFastLocalOcr(payload);
          rawText = localResult.text?.trim() || "";
        } else {
          throw new Error(data.error || `Failed to extract text from ${isPdf ? "PDF" : "image"}.`);
        }
      } catch (err: any) {
        if (isImage) {
          const { runFastLocalOcr } = await import("./fastOcr");
          const localResult = await runFastLocalOcr(payload);
          rawText = localResult.text?.trim() || "";
        } else {
          throw err;
        }
      }

      const consumedBytes = file.size || estimateBase64SizeBytes(payload);
      recordDailyComputeUsage(consumedBytes, isPdf ? "pdf" : "image");

      if (!rawText || rawText === "NO_TEXT_DETECTED") {
        return {
          success: false,
          text: "",
          filename,
          fileType: isPdf ? "pdf" : "image",
          wordCount: 0,
          characterCount: 0,
          error: `No readable text could be recognized in ${filename}.`
        };
      }

      const clean = sanitizeExtractedContent(rawText);
      const words = countWords(clean);

      return {
        success: true,
        text: clean,
        filename,
        fileType: isPdf ? "pdf" : "image",
        wordCount: words,
        characterCount: clean.length
      };
    }

    // Fallback attempt: try reading as plain text if under 2MB
    if (file.size < 2 * 1024 * 1024) {
      const rawText = await file.text();
      if (rawText.trim() && !isBinaryText(rawText)) {
        const clean = sanitizeExtractedContent(rawText);
        return {
          success: true,
          text: clean,
          filename,
          fileType: "text",
          wordCount: countWords(clean),
          characterCount: clean.length
        };
      }
    }

    return {
      success: false,
      text: "",
      filename,
      fileType: "text",
      wordCount: 0,
      characterCount: 0,
      error: `Unsupported file format (${file.type || filename}). Supported formats: .docx, .pdf, .txt, .md, and images (.png, .jpg).`
    };
  } catch (err: any) {
    return {
      success: false,
      text: "",
      filename,
      fileType: "text",
      wordCount: 0,
      characterCount: 0,
      error: err?.message || "An unexpected error occurred while parsing the document."
    };
  }
}

function sanitizeExtractedContent(raw: string): string {
  const injection = detectAndSanitizePromptInjection(raw);
  return sanitizeUserInput(injection.sanitizedText);
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read file from disk."));
    reader.readAsDataURL(file);
  });
}

function isBinaryText(text: string): boolean {
  // Checks if text contains suspicious null bytes or binary sequences
  const sample = text.slice(0, 1000);
  let nullCount = 0;
  for (let i = 0; i < sample.length; i++) {
    if (sample.charCodeAt(i) === 0) nullCount++;
  }
  return nullCount > 2;
}

/**
 * Extracts a File (image or PDF) from clipboard data if present.
 * Returns null if the clipboard only contains plain text or unsupported content.
 */
export function extractClipboardFile(clipboardData: DataTransfer | null): File | null {
  if (!clipboardData) return null;

  // 1. Check items for image or PDF blob
  if (clipboardData.items && clipboardData.items.length > 0) {
    for (let i = 0; i < clipboardData.items.length; i++) {
      const item = clipboardData.items[i];
      if (item.type.startsWith("image/")) {
        const blob = item.getAsFile();
        if (blob) {
          const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(11, 19);
          return new File([blob], `Pasted-Screenshot-${timestamp}.png`, { type: blob.type || "image/png" });
        }
      } else if (item.type === "application/pdf") {
        const blob = item.getAsFile();
        if (blob) {
          return new File([blob], "Pasted-Document.pdf", { type: "application/pdf" });
        }
      }
    }
  }

  // 2. Check files array
  if (clipboardData.files && clipboardData.files.length > 0) {
    for (let i = 0; i < clipboardData.files.length; i++) {
      const file = clipboardData.files[i];
      if (file.type.startsWith("image/") || file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
        return file;
      }
    }
  }

  return null;
}

